import { useEffect } from 'react';
import { X, Terminal } from '@phosphor-icons/react';
import { useDevConsole, type DevConsoleTab } from '~/lib/stores/ui-store';
import { Button } from '~/components/ui/button';
import { Tabs, TabsList, TabsTrigger, useTabPanel } from '~/components/ui/tabs';
import { SandboxDebug } from '~/features/settings/sandbox-debug';

/**
 * Bottom-anchored dev console with a tabbed interactive surface — modeled
 * after VS Code's integrated terminal / Cursor's command palette. Sandbox
 * playground is the first tenant; future tabs (Restate state, SQL,
 * cron force-run, log tail) plug into the same chassis.
 *
 * Toggle: Cmd/Ctrl+J (matches VS Code's terminal binding — single-key,
 * keyboard-layout-friendly, unlike the `\` we tried first). Esc closes.
 */

interface Tab {
  id: DevConsoleTab;
  label: string;
  render: () => React.ReactElement;
}

const TABS: Tab[] = [
  { id: 'sandbox', label: 'Sandbox', render: () => <SandboxDebug /> },
];

export function DevConsole() {
  const { open, tab, toggle, setTab } = useDevConsole();
  const tabPanel = useTabPanel(tab);

  // Global keyboard shortcut: Cmd/Ctrl + J (same as VS Code's terminal)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        toggle();
      }
      if (e.key === 'Escape' && open) {
        // Don't steal escape from inputs — only close when the focus is
        // outside an editable element. Otherwise the user trying to bail
        // out of a textarea also dismisses the panel they're working in.
        const t = e.target as HTMLElement | null;
        const editable =
          !!t &&
          (t.tagName === 'INPUT' ||
            t.tagName === 'TEXTAREA' ||
            t.isContentEditable);
        if (!editable) toggle();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, open]);

  if (!open) return null;

  const active = TABS.find((t) => t.id === tab) ?? TABS[0];

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 bg-card border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.15)] flex flex-col"
      style={{ height: 'min(50vh, 540px)' }}
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5 bg-background/40 shrink-0">
        <div className="flex items-center gap-1">
          <Terminal size={14} className="text-muted-foreground mr-1.5" />
          {/* FRONT-2485: the shared Tabs look, `sm` for the console's slim bar. */}
          <Tabs value={tab} onValueChange={(v) => setTab(v as DevConsoleTab)}>
            <TabsList size="sm">
              {TABS.map((t) => (
                <TabsTrigger key={t.id} value={t.id} className="flex-none" {...tabPanel.trigger(t.id)}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[length:var(--text-2xs)] text-muted-foreground/60 tabular-nums hidden sm:inline">
            ⌘J
          </span>
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={toggle}
            tooltip="Close (⌘J)"
            aria-label="Close dev console"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
          >
            <X size={14} />
          </Button>
        </div>
      </div>
      <div {...tabPanel.panel} className="flex-1 overflow-y-auto px-4 py-3">
        {active.render()}
      </div>
    </div>
  );
}
